import { CrudStoreItem } from 'legacy/lut/classes/models/CrudStoreItem'
import { DataModel } from 'legacy/lut/classes/models/DataModel'
import {
  CreateAdditionalByModel,
  CreateExtByModel,
  IFormProfile,
  modifiedFieldsExt,
} from 'legacy/lut/classes/models/IFormProfile'
import { appGetState } from 'legacy/lut/store'
import { lookupGetStateOld, read, save, setNextLookupState } from 'legacy/lut/store'
import { IBusinessProfileCollection, StoreForms } from 'legacy/lut/store/state'

import { AppId } from 'clickNgo/configs/app'
import { IForm } from 'core-components/classes/models/IForm'
import { IDataProviderFilter, Logic, Operator } from 'core-components/types'
import { IFetchResult } from 'core/classes/helpers/store/Interfaces'
import { HttpCancel } from 'core/classes/http'
import { IDataTableSettings } from 'core/classes/models/IDataTable'
import { MarketApi } from 'core/classes/services/api/MarketApi'
import { siteBuilderGetState } from 'core/sitebuilder/store'
import { CrudStore } from 'core/types/crudStore'
import { createTableFilter } from 'core/types/helpers/createTableFilter'
import { contactService } from 'onboarding/services/contactService'
import { mapContactFromCrud } from 'onboarding/services/mapping/mapContactFromCrud'

import { IFormProfileExt } from '../models/IFormProfileExt'

import businessProfileApi from './api/BusinessProfileApi'

export class BusinessProfileStoreService {
  public nextLookupState = setNextLookupState
  public appGetState = appGetState
  public lookupGetStateOld = lookupGetStateOld
  public loading: Promise<void>

  public modifiedFieldsExt = modifiedFieldsExt

  public constructor() {
    this.loading = new Promise<void>((resolve) => {
      this.loadingResolver = resolve
    })
  }

  public loadingResolver: () => void = () => {}

  public init = async (cancelObject: HttpCancel) => {
    const state = this.lookupGetStateOld()
    const region = MarketApi.getRegion(appGetState().user.geoMarket).Region
    let saved = false

    ;(Object.keys(state.businessProfileWidget) as Array<keyof IBusinessProfileCollection>).map(
      (key) => {
        if (state.businessProfileWidget[key]?.length) {
          saved = true
        }
      }
    )

    const { relationship, changeMindset, roles, status, seniority } = saved
      ? state.businessProfileWidget
      : await businessProfileApi.fetchStaticData(cancelObject)
    const countriesCode = read(StoreForms.dropdowns, `countriesCode_${region}`)
    if (!countriesCode) {
      const getCountriesCode = await businessProfileApi.fetchCountriesCode(cancelObject)
      if (!cancelObject.canceled) {
        save(StoreForms.dropdowns, `countriesCode_${region}`, {
          items: getCountriesCode,
          total: getCountriesCode.length || 0,
        })
      }
    }

    this.nextLookupState({
      ...this.lookupGetStateOld(),
      businessProfileWidget: {
        ...this.lookupGetStateOld().businessProfileWidget,
        relationship,
        changeMindset,
        roles,
        status,
        seniority,
      },
    })
  }

  public fetchConnector = async (data: IForm[]) => {
    if (data) {
      data.forEach((el) => this.profilesPreprocess(el))
    }
    return data
  }

  public localPrefPromices: {
    [key: string]: Promise<IFetchResult<IFormProfile>>
  } = {}

  public getFilterForSearch = (search: string) => {
    const fields = ['FirstName', 'LastName', 'ContactID', 'Email']
    const phoneNumbers = ['MainPhone', 'MobilePhone', 'WorkPhone']
    const cleared = search && search.replace(/\D+/g, '')
    let phoneFilters: {
      field: string
      operator: Operator
      value: string
    }[] = []
    if (cleared) {
      phoneFilters = phoneNumbers.map((field) => ({
        field,
        operator: Operator.CONTAINS,
        value: cleared,
      }))
    }
    return {
      logic: Logic.OR,
      filters: fields
        .map((field) => ({
          field,
          operator: Operator.CONTAINS,
          value: search,
        }))
        .concat(phoneFilters),
    }
  }

  public getBasicFilter = (props: any) => {
    const { selectedPartner, pid, onlyOpened } = props

    const filter = {
      logic: Logic.AND,
      filters: [
        {
          field: 'ContactID',
          operator: Operator.NOTEQUAL,
          value: null,
        },
        {
          field: selectedPartner ? 'PartnerID' : 'TopPartnerID',
          operator: Operator.EQUAL,
          value: selectedPartner || pid,
        },
      ],
    }

    if (onlyOpened) {
      return null
    }

    return filter
  }

  public getActiveFilter = (onlyActive: boolean) => {
    return onlyActive
      ? {
          field: 'Status',
          operator: Operator.EQUAL,
          value: 'Active',
        }
      : undefined
  }

  public createFilter = (
    props: any,
    filters: any,
    settings?: IDataTableSettings | null | undefined
  ): IDataProviderFilter => {
    const innerFilters: any = filters.showNew
      ? []
      : this.getBasicFilter(props) === null
        ? []
        : [this.getBasicFilter(props)]
    if (filters.searchString) {
      innerFilters.push(this.getFilterForSearch(filters.searchString))
    }
    if (!filters.showNew) {
      const showActive = this.getActiveFilter(!filters.showInactive)
      if (showActive) {
        innerFilters.push(showActive)
      }
    }
    if (settings) {
      const settingsFilters = createTableFilter(settings)
      if (settingsFilters && Object.keys(settingsFilters).length > 0) {
        innerFilters.push(settingsFilters)
      }
    }
    return {
      logic: Logic.AND,
      filters: innerFilters,
    }
  }

  private profilesPreprocess = (partner: IFormProfile) => {
    if (partner.WorkflowStatus && partner.WorkflowStatus === 'Done') {
      const extFields = Object.keys(partner).filter((key) => key.endsWith('Ext'))
      extFields.forEach((fieldName) => {
        if (fieldName === 'EntryIdExt') {
          return
        }
        const withoutExtName = fieldName.substr(0, fieldName.length - 3)
        partner[fieldName] = partner[withoutExtName]
      })
      partner.ModifiedValues = ''
    }
  }

  public getRegDashboardReal = async (contactId: string, cancelObject: HttpCancel) => {
    const state = this.lookupGetStateOld()

    let { regDashboard } = state.businessProfileWidget
    let findRegistrationItem = regDashboard.find((item: any) => item['ContactID'] === contactId)

    if (!findRegistrationItem) {
      const contactIds = new Set<string>([contactId])
      const regDashboardPart = await businessProfileApi.fetchRegDashboardReal(
        contactIds,
        cancelObject
      )

      regDashboard = regDashboard.concat(regDashboardPart)

      this.nextLookupState({
        ...state,
        businessProfileWidget: {
          ...state.businessProfileWidget,
          regDashboard,
        },
      })

      findRegistrationItem = (regDashboardPart || [])[0] || {}
    }
    return {
      Date: findRegistrationItem['InvitedDate'],
      Status: findRegistrationItem['Status'],
      EnrollmentId: findRegistrationItem['EntryID'],
    }
  }

  public getUserRightsAndCampaigns = async (
    contactID: string[],
    cancelObject: HttpCancel,
    pageSize?: number
  ) => {
    if (contactID.length) {
      const crudUserRight = CrudStore.get(CrudStoreItem.WW_UserRights, 'getUserRightsAndCampaigns')
      const filterUserRight: IDataProviderFilter = {
        logic: Logic.AND,
        filters: [],
      }
      contactID.forEach((contactId) => {
        filterUserRight.filters[filterUserRight.filters.length] = {
          field: 'CONTACT_ID',
          operator: Operator.EQUAL,
          value: contactId,
        }
      })
      crudUserRight.setPaging({
        page: 0,
        pageSize: pageSize || 5,
      })
      crudUserRight.setFilter(filterUserRight)
      const userRights = await crudUserRight.fetch()

      const filterCampaigns: IDataProviderFilter = {
        logic: Logic.AND,
        filters: [],
      }
      contactID.forEach((contactId) => {
        filterCampaigns.filters[filterCampaigns.filters.length] = {
          field: 'CONTACT_ID',
          operator: Operator.EQUAL,
          value: contactId,
        }
      })

      const crudCampaigns = CrudStore.get(
        CrudStoreItem.WW_ContactCampaigns,
        'getUserRightsAndCampaigns'
      )
      crudCampaigns.setSort({ field: 'CAMPAIGNS', dir: 'asc' })
      crudCampaigns.setPaging({
        page: 0,
        pageSize: pageSize || 5,
      })
      crudCampaigns.setFilter(filterCampaigns)
      const contactCampaigns = await crudCampaigns.fetch()

      return {
        userRights,
        contactCampaigns,
        contactID: contactID.join(),
        userRightsPage: 1,
        contactCampaignsPage: 1,
      }
    }
    return
  }

  public getToolsByContactIds = async (contactIds: string[]) => {
    if (contactIds.length) {
      const filter: IDataProviderFilter = {
        logic: Logic.AND,
        filters: [],
      }
      contactIds.forEach((contactId) => {
        filter.filters[filter.filters.length] = {
          field: 'ContactID',
          operator: Operator.EQUAL,
          value: contactId,
        }
      })
      const pageSize = 100 * contactIds.length

      const crud = CrudStore.get(CrudStoreItem.WW_UserToolsAccess, 'getToolsByContactIds')
      crud.setFilter(filter)
      crud.setPaging({
        page: 0,
        pageSize,
      })
      const result = await crud.fetch()

      return result
    }
    return
  }

  public validateContact = async (changedProfile: DataModel) => {
    const profileExt = CreateExtByModel(changedProfile)

    if (profileExt) {
      const contact = mapContactFromCrud(profileExt)
      const result = await contactService.validateContact(contact)

      return result
    }

    return { success: false }
  }

  public changeProfileExt = async (
    changedProfile: DataModel,
    workflowStatus?: string,
    appSource?: string,
    sourceRequestor?: string
  ): Promise<IFormProfile | IFormProfileExt | null> => {
    const contactExtOptions = { formId: '375', code: 'ContactExt' }
    const contactAdditionalInfoOptions = { formId: '379', code: 'ContactAdditionalInfo' }
    const isNewContact =
      changedProfile.get('ContactID') && changedProfile.get('ContactID').indexOf('new-') === 0
    const hasExtFields = changedProfile.modified.some(
      (el) => el.endsWith('Ext') || Object.keys(changedProfile.rawData).indexOf(`${el}Ext`)
    )
    const isExtModified = isNewContact || hasExtFields
    const profileWorkflowStatus = changedProfile.get('WorkflowStatus') || 'New'
    const newStatus = isExtModified
      ? workflowStatus || 'New'
      : workflowStatus || profileWorkflowStatus
    const isRejected = newStatus === 'Rejected'
    const isDone = newStatus === 'Done'
    const crud = CrudStore.get(
      isNewContact ? CrudStoreItem.ContactExt : CrudStoreItem.ContactAllData
    )
    const page: string = siteBuilderGetState().routeParams.page

    const isLut = siteBuilderGetState().app.id === AppId.Lut

    const clickAndGoPages = ['newpartners', 'partner-dashboard']

    const p360Pages = ['jbl-hpscoutso']

    let source = appSource || 'Onboarding'

    if (!isLut && clickAndGoPages.some((el) => page.includes(el)) && !page.includes('request')) {
      source = 'Click&Go'
    }

    if (isLut) {
      source = 'Lut'
    }

    if (p360Pages.some((el) => page.includes(el)) && !page.includes('request')) {
      source = 'P360'
    }

    if (source && !changedProfile.get('Source')) {
      changedProfile.set('Source', source)
    }
    if (profileWorkflowStatus === 'New') {
      const currentSource = changedProfile.get('Source')

      if (currentSource && !currentSource.endsWith(' Portal')) {
        changedProfile.set('Source', `${currentSource} Portal`)
      }
      changedProfile.set('InternalWorkflowStatus', 'Scheduled')
    }
    if (sourceRequestor) {
      changedProfile.set('SourceRequestor', sourceRequestor)
    }
    const profileExt = CreateExtByModel(changedProfile)
    let changedProfileExt
    if (isExtModified && profileExt && profileExt.ContactID) {
      const operationType = profileExt.EntryId ? 'update' : 'create'

      profileExt['WorkflowStatus'] = newStatus
      if (isRejected) {
        if (profileExt.EntryId) {
          await crud.update(contactExtOptions, [{ EntryId: profileExt.EntryId }])
        }
        await crud.delete(contactExtOptions, profileExt, 'EntryId')
        profileExt.EntryId = undefined
      } else if (isDone) {
        if (profileExt.EntryId) {
          await crud.update(contactExtOptions, [
            { EntryId: profileExt.EntryId, LastRequest: 'false' },
          ])
        }
        profileExt.EntryId = undefined
      }

      const result = await crud[operationType](contactExtOptions, [profileExt])
      changedProfileExt = result[0]
      if (changedProfileExt) {
        changedProfile.set('EntryIdExt', changedProfileExt.EntryId)
        changedProfile.set('WorkflowStatus', changedProfileExt.WorkflowStatus)
      }
    }

    let changedProfileAdditional
    if (!isNewContact) {
      const profileAdditional = CreateAdditionalByModel(changedProfile)
      const operationType = profileAdditional.EntryId ? 'update' : 'create'
      const result = await crud[operationType](contactAdditionalInfoOptions, [profileAdditional])
      changedProfileAdditional = result[0]
      if (changedProfileAdditional) {
        changedProfile.set('EntryIdAdd', changedProfileAdditional.EntryId)
      }
    }

    changedProfile.set(
      'Modified',
      (changedProfileExt && changedProfileExt.Modified) ||
        (changedProfileAdditional && changedProfileAdditional.Modified)
    )
    this.loadingResolver()
    return changedProfileExt
  }

  public getContactBymail = async (
    email: string,
    key: string
  ): Promise<IFormProfile | IFormProfileExt | null> => {
    const sourceExists = CrudStore.get(CrudStoreItem.WW_Contacts, key)
    const sourceNew = CrudStore.get(CrudStoreItem.ContactExt, key)
    sourceExists.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'Email',
          operator: Operator.EQUAL,
          value: email,
        },
      ],
    })
    const resultExists = await sourceExists.fetch()
    if (resultExists.items.length) {
      return resultExists.items[0]
    }
    sourceNew.setFilter({
      logic: Logic.AND,
      filters: [
        {
          field: 'Email',
          operator: Operator.EQUAL,
          value: email,
        },
      ],
    })
    const resultNew = await sourceNew.fetch()
    if (resultNew.items.length) {
      return resultNew.items[0]
    }
    return null
  }
}

export default new BusinessProfileStoreService()
